import { takeLatest, put } from 'redux-saga/effects';
import { call, select } from 'typed-redux-saga';
import * as api from 'data/api';
import { ExtraServiceCheckoutModal } from 'constants/extra-service';
import { logInPushUpScope } from 'components/Bumps';
import { getCookie, saveCookie } from 'libs/utils/cookies';
import { createSagaErrorBoundary } from 'libs/utils/sagaErrorBoundary';
import { actions } from './slice';
import * as selectors from './selectors';
import * as statelessActions from './actions';
import { VALUE_PROPOSITION_SHOWN_COOKIE_NAME } from './constants';
import { transformPushUpOrder, transformPushUpsAvailability, transformPushUpsOptions } from './transformers';
const callWithErrorLogging = createSagaErrorBoundary(logInPushUpScope);
export function* getPushUpsOptions() {
  const interationalBumpsTestVariant = yield* select(selectors.getInternationalPushUpsTestVariant);
  const itemIds = yield* select(selectors.getSelectedIds);

  if (interationalBumpsTestVariant === 'off' || !itemIds.length) {
    return;
  }

  const response = yield* call(api.getPushUpsOptions, {
    itemIds
  });

  if ('errors' in response) {
    return;
  }

  const pushUpsAvailability = transformPushUpsOptions(response);
  yield put(actions.getPushUpsOptionsSuccess({
    pushUpsAvailability
  }));
}
export function* getPushUpsAvailability() {
  const selectedItemIds = yield* select(selectors.getSelectedIds);
  const response = yield* call(api.getPushUpsAvailability, {
    itemsCount: selectedItemIds.length
  });

  if ('errors' in response) {
    yield put(statelessActions.getPushUpsAvailabilityFailure());
    return;
  }

  const pushUpsAvailabilityDto = response.push_up_availability;
  const pushUpsAvailability = transformPushUpsAvailability(pushUpsAvailabilityDto);
  yield put(actions.getPushUpsAvailabilitySuccess({
    pushUpsAvailability
  }));
  yield* call(getPushUpsOptions);
}
export function* preparePushUpOrder() {
  yield put(actions.preparePushUpOrderRequest());
  const itemIds = yield* select(selectors.getSelectedIds);
  const pushUpOption = yield* select(selectors.getSelectedPushUpOption);

  if (!pushUpOption) {
    yield put(actions.preparePushUpOrderFailure());
    return;
  }

  const response = yield* call(api.preparePushUpOrder, itemIds, pushUpOption.days, pushUpOption.international);

  if ('errors' in response) {
    yield put(actions.preparePushUpOrderFailure());
    return;
  }

  const pushUpOrder = response.push_up_order;
  yield put(actions.preparePushUpOrderSuccess({
    pushUpOrder: transformPushUpOrder(pushUpOrder)
  }));
}
export function* getPushUpOrder(_ref) {
  let {
    payload: {
      pushUpOrderId
    }
  } = _ref;
  const response = yield* call(api.getPushUpOrder, pushUpOrderId);

  if ('errors' in response) {
    yield put(actions.getPushUpOrderFailure());
    return;
  }

  const pushUpOrder = transformPushUpOrder(response.push_up_order);
  yield put(actions.getPushUpOrderSuccess({
    pushUpOrder
  }));
}
export function* startCheckout(_ref2) {
  let {
    payload: {
      selectedItemId
    }
  } = _ref2;

  if (selectedItemId) {
    yield put(actions.setSelectedItems({
      itemIds: [selectedItemId]
    }));
  }

  const isValuePropositionSeen = getCookie(VALUE_PROPOSITION_SHOWN_COOKIE_NAME);
  const showValueProposition = !isValuePropositionSeen || isValuePropositionSeen === 'false';

  if (showValueProposition) {
    saveCookie(VALUE_PROPOSITION_SHOWN_COOKIE_NAME, 'true');
    yield put(actions.setCheckoutModal({
      modal: ExtraServiceCheckoutModal.ValueProposition
    }));
    return;
  }

  yield put(actions.setCheckoutModal({
    modal: ExtraServiceCheckoutModal.OrderReview
  }));
}
export default function* saga() {
  yield takeLatest(statelessActions.startCheckout.type, startCheckout);
  yield takeLatest([statelessActions.startCheckout.type, actions.setSelectedPushUpOption.type], callWithErrorLogging(preparePushUpOrder));
  yield takeLatest([statelessActions.getPushUpsAvailabilityRequest.type, actions.setSelectedItems], callWithErrorLogging(getPushUpsAvailability));
  yield takeLatest(actions.getPushUpOrderRequest.type, getPushUpOrder);
}